// Option C (2026-10-11): the per-fund FundBacktestPanel configs, referenced
// from the fund page registry (FUND_PAGES[slug].backtestPanel). Every value
// here is what GoldBacktestPanel.vue / GgeBacktestPanel.vue used to
// hard-code.
import { markRaw, type Component } from 'vue'
import type { BacktestParams } from '../../api/client'
import type { BacktestAssetId, BacktestConfigFund } from '../nav/fundConfig'
import GoldBacktestKnobs from './GoldBacktestKnobs.vue'
import GgeBacktestKnobs from './GgeBacktestKnobs.vue'

export interface FundBacktestConfig {
  /** Backtest asset id (api fund= / assetId) and data-testid prefix. */
  assetId: BacktestAssetId & BacktestConfigFund
  /** Legacy root class kept alongside .fund-backtest (gold-backtest / gge-backtest). */
  rootClass: string
  title: string
  /** Form defaults; a loaded preset is merged over these. */
  defaultParams: BacktestParams
  /** The fund's knob section(s); props { params: BacktestParams; bandError: string | null }. */
  knobs: Component
  /** The "stack: …" line under the run button. */
  stackLabel: (p: BacktestParams) => string
  /** Tooltip on the walk-forward "Full grid" checkbox. */
  fullGridTitle: string
  /** Caption under the superuser "Apply … to Live" button. */
  applyCaveat: string
}

export const GOLD_BACKTEST_PANEL: FundBacktestConfig = {
  assetId: 'gold',
  rootClass: 'gold-backtest',
  title: 'Gold Backtest — Ninety One Global Gold',
  defaultParams: {
    zWindow: 120,
    tiltBps: 20,
    vixScale: 1.0,
    termStructure: true,
    nfpFilter: false,
    holidayFilter: true,
    opexFilter: false,
    quadWitchingFilter: false,
    exDividendFilter: false,
    futuresRollFilter: true,
    eomInterestFilter: false,
    eomInterestBoostBps: 29,
    noHold: false,
    imrMode: 'off',
    imrTiltBps: 0,
    imrWindow: 5,
    imrBreadthThreshold: 0.5,
    vixMomGate: 'off',
    autoOptimize: true,
    useHedging: false,
    useOverlay: false,
    useShortConviction: false,
    useLongConviction: false,
    otpReservePct: 0,
    yieldCollapseGateMode: 'off',
    yieldCollapseDropBps: 50,
    yieldCollapseWindowDays: 20,
    stepAsideMode: 'off',
    stepAsideThresholdBps: 0,
    // 2026-09-15: null = legacy one-sided rule. Non-null opts into the
    // dead zone; must be >= the sell threshold (guard: stepAsideBandError).
    stepAsideBuyThresholdBps: null as number | null,
    stepAsideReentryBps: null,
    // Default ON for gold: pre-Databento days (2008 → 2010-06-14) have no GC
    // intraday print, the strategy fail-safes to hold, and full-period metrics
    // are unrealistic (Sharpe 1.77 vs clean-window 2.27).
    postDatabentoOnly: true,
  },
  knobs: markRaw(GoldBacktestKnobs),
  stackLabel: (p) => {
    const parts: string[] = []
    parts.push(p.postDatabentoOnly ? 'window 2010+' : 'window FULL (incl. pre-Databento)')
    parts.push(`yield gate ${p.yieldCollapseGateMode === 'on' ? `on (${p.yieldCollapseDropBps}bps/${p.yieldCollapseWindowDays}d)` : 'off'}`)
    parts.push(`step-aside ${p.stepAsideMode}`)
    return `stack: ${parts.join(' · ')}`
  },
  fullGridTitle: "Off: 24-combo grid (~30min-3h). On: 240-combo grid (~25h on gold's 16y window) — was the default until 2026-09-15 and produced a 42-fold runaway.",
  applyCaveat: 'Promotes to config:mr:gold Redis keys + backtest_configs/gold.json. "Hold unless futures down" '
    + 'trades on every overnight print below the SELL bar — verify expected turnover first.',
}

// GGE (WS Guinness Global Energy) is a pure step-aside strategy driven by the
// CL 08:40 signal (GuinnessEnergyAssetProfile.StepAsideSignalCsvFilename =
// cl_0840_signal.csv, gge_mt_signal.py). All OTP/FVA/canonical-synth-nav
// controls are deliberately OMITTED — the profile's overlay ETPs are
// placeholders (XLE3L_PLACEHOLDER / XLE3S_PLACEHOLDER) that MUST never trade,
// mirroring GuinnessEnergyOrchestrator.Sanitize; the API's SanitizeForAsset
// hard-clears the same list at job dispatch so a stale preset cannot smuggle
// them through.
export const GGE_BACKTEST_PANEL: FundBacktestConfig = {
  assetId: 'gge',
  rootClass: 'gge-backtest',
  title: 'GGE Backtest — WS Guinness Global Energy',
  defaultParams: {
    zWindow: 90,
    tiltBps: 40,
    vixScale: 1.0,
    termStructure: false,
    nfpFilter: false,
    holidayFilter: true,
    opexFilter: true,
    quadWitchingFilter: true,
    exDividendFilter: true,
    futuresRollFilter: true,
    eomInterestFilter: true,
    eomInterestBoostBps: 15,
    noHold: false,
    // 2026-10-13: 'off' (was 'filter'). gge_mt_signal never implements IMR, and in pure step-aside the
    // backtest never reaches the gate either (docs/analysis/2026-10-11-imr-filter-inert-in-pure-step-aside.md).
    imrMode: 'off',
    imrTiltBps: 0,
    imrWindow: 10,
    imrBreadthThreshold: 0.5,
    vixMomGate: 'off',
    autoOptimize: true,
    useHedging: false,
    useOverlay: false,
    useShortConviction: false,
    useLongConviction: false,
    otpReservePct: 0,
    // Step-aside is the primary GGE control. Default matches the shipped
    // gge_futures_driven preset: pure-mode with threshold 0 / memoryless
    // re-entry (TPE swept the threshold and chose 0 — 2026-08-18 verdict).
    stepAsideMode: 'pure',
    stepAsideThresholdBps: 0,
    stepAsideBuyThresholdBps: null as number | null,
    stepAsideReentryBps: null,
    // postDatabentoOnly is a BGA/gold construct (Databento intraday-print
    // coverage floor 2010-06-14) — semantics don't map to GGE. GGE has its
    // own manager-era floor: guinnessEraOnly (below).
    postDatabentoOnly: false,
    // The Guinness-era clamp: drops rows before 2019-08-01, when WS took over
    // management from Artemis. The study's authoritative Sharpe 2.18 screen /
    // 2.23 CPCV / CAGR 47.7% (docs/analysis/2026-08-18-guinness-energy-verdict.md)
    // come from this window; running the pre-2019 Artemis-era rows drags
    // Sharpe to ~1.46 without changing the strategy. Default ON to match the
    // CLI orchestrator's ws_guinness dataset.
    guinnessEraOnly: true,
  },
  knobs: markRaw(GgeBacktestKnobs),
  stackLabel: (p) => `stack: step-aside ${p.stepAsideMode} (threshold ${p.stepAsideThresholdBps}bps)`,
  fullGridTitle: 'Off: 24-combo grid (~30min-3h). On: 240-combo grid (~5-25h) — was the default until 2026-09-15.',
  applyCaveat: 'Promotes to config:mr:gge Redis keys + backtest_configs/gge.json. '
    + '"Hold unless futures down" trades on every 08:40 CL print below the SELL bar — verify expected turnover first.',
}
